import { FieldPath, FieldValues, RegisterOptions, useFormContext } from "react-hook-form";

import { FilterableSinglePickerFormField } from "src/components/Form/FilterableSinglePickerFormField";
import { useGetIdentifiersQuery } from "src/graphql/anti-fraud/queries/rules/IdentifiersQuery.ts";
import { useT } from "src/internationalization";

export type IdentifierFilterableSinglePickerFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  id: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
  caseTypeCode?: string;
};

export function IdentifierFilterableSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  id,
  name,
  rules,
  label,
  helperText,
  placeholder,
  caseTypeCode,
}: IdentifierFilterableSinglePickerFormFieldProps<TFieldValues, TName>) {
  const t = useT();
  const { setError, control } = useFormContext<TFieldValues, TName>();

  const { data: items, loading } = useGetIdentifiersQuery({
    onError: (error) => setError(name, error),
    caseTypeCode,
  });

  return (
    <FilterableSinglePickerFormField
      id={id}
      control={control}
      name={name}
      rules={rules}
      label={label}
      helperText={helperText}
      options={items}
      getOptionLabel={(item) =>
        item.clientType === "customer"
          ? `${t("component.picker.identifier.customer")} - ${item.elementName} - ${
              item.identifierName
            }`
          : `${item.elementName} - ${item.identifierName}`
      }
      getOptionValue={(item) => `${item.clientType}-${item.identifierCode}-${item.elementCode}`}
      loading={loading}
      placeholder={placeholder}
    />
  );
}
